import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { GenerateAiPetitionDraft } from './api.graphql';
import type { GenerateAiPetitionDraftMutation, GenerateAiPetitionDraftMutationVariables } from './api.graphql';

type Input = { mainPrompt: string; personalStory?: string; locationName?: string };

// HugeRTE represents paragraphs as <p> blocks (one per Enter keypress), so the AI draft
// must use <p> blocks too — otherwise the whole description lands in a single <p> full of
// <br>s, which breaks new-line behavior in the editor and renders poorly in petition details.
function toHtmlParagraphs(text: string): string {
	return text
		.split(/\n{2,}/)
		.map((paragraph) => paragraph.trim())
		.filter((paragraph) => paragraph.length > 0)
		.map((paragraph) => `<p>${paragraph.replaceAll('\n', '<br>')}</p>`)
		.join('');
}

type GenerateAiDescriptionReturn = { ask: string; description: string };
export async function generateAiPetitionDraft(
	input: Input,
	{ gql: { fetch } }: UtilityContext,
	track: TrackingFn,
): Promise<GenerateAiDescriptionReturn> {
	void track('sap_ai_draft_generate');

	try {
		const { data } = await fetch<GenerateAiPetitionDraftMutation, GenerateAiPetitionDraftMutationVariables>({
			query: GenerateAiPetitionDraft,
			path: '/generate-ai-petition-draft',
			variables: { input },
			rejectOnError: true,
		});

		if (data.generateAiPetitionDraft.__typename === 'GenerateAiDraftError')
			throw Error(data.generateAiPetitionDraft.reason);

		void track('sap_ai_draft_generate_loaded');

		const recommendedDraft = {
			ask: data.generateAiPetitionDraft.ask,
			// The description is HTML-stripped server-side; the LLM separates paragraphs
			// with a blank line (\n\n) and uses single \n for soft breaks. Map those to
			// <p> blocks + <br> so the rich text editor and petition details render properly.
			description: toHtmlParagraphs(data.generateAiPetitionDraft.description),
		};

		return recommendedDraft;
	} catch (e: unknown) {
		void track('sap_ai_draft_generate_error');

		throw new Error('SAP: Failed to generate AI description', { cause: e });
	}
}
